import { type Audit, AuditStatus } from '@compass/client/types/audit';
import { Button } from '@components/ui/button';
import { Copy } from 'lucide-react';
import React, { useMemo } from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { useUsersByIds } from '@/hooks/useUsers';
import { copyToClipboard } from '@/utils/clipboard';

import type { PopoverContentProps, TabLabelContext, TabTypeHandler } from './types';

// Human Audit tab (BI-1246). Mirrors the AI Audit tab — both render audit
// corrections over the case output via the case details page — but surfaces
// audits authored by a human reviewer (AUDIT_TYPE_HUMAN), with its own label
// and numbering so it never collides with the AI Audit tab.

const getLabel = (_data: Audit, context: TabLabelContext): string => {
  // Suffix the position only when the case has more than one human audit, so a
  // single-audit case keeps the original "Human Audit" label.
  return context.totalOfType > 1 ? `Human Audit ${context.attemptNumber}` : 'Human Audit';
};

// formatDuration renders the elapsed time between two ISO timestamps as a compact
// "1h 2m 3s" string (BI-1246: time the auditor spent in the human-audit session).
const formatDuration = (startISO?: string, endISO?: string): string | null => {
  if (!startISO || !endISO) return null;
  const start = new Date(startISO).getTime();
  const end = new Date(endISO).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  const totalSec = Math.round((end - start) / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
};

const AUDIT_STATUS_LABEL: Record<AuditStatus, string> = {
  [AuditStatus.AUDIT_STATUS_UNSPECIFIED]: 'Unspecified',
  [AuditStatus.AUDIT_STATUS_IN_PROGRESS]: 'In progress',
  [AuditStatus.AUDIT_STATUS_COMPLETED]: 'Completed',
  [AuditStatus.AUDIT_STATUS_FAILED]: 'Failed',
  [AuditStatus.AUDIT_STATUS_SKIPPED]: 'Skipped',
  [AuditStatus.AUDIT_STATUS_CANCELLED]: 'Cancelled',
};

const HumanAuditPopoverContent: React.FC<PopoverContentProps<Audit>> = ({ data: audit }) => {
  // Duration the auditor spent in the session: finished_at - created_at.
  const duration = formatDuration(audit.created_at, audit.finished_at);

  // Resolve the auditor's hex user ID to a display name.
  const auditorIds = useMemo(
    () => (audit.auditor_user_id ? [audit.auditor_user_id] : []),
    [audit.auditor_user_id],
  );
  const { usersMap } = useUsersByIds(auditorIds, !!audit.auditor_user_id);
  const auditorName = audit.auditor_user_id
    ? (usersMap.get(audit.auditor_user_id)?.name ??
      usersMap.get(audit.auditor_user_id)?.email ??
      audit.auditor_user_id)
    : null;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between gap-8 items-center">
        <span className="text-muted-foreground">Audit ID:</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{audit.id}</span>
          {audit.id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => audit.id && copyToClipboard(audit.id)}
              className="h-6 w-6 p-0"
            >
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-between gap-8">
        <span className="text-muted-foreground">Type:</span>
        <span>Human Audit</span>
      </div>
      {auditorName && (
        <div className="flex justify-between gap-8 items-center">
          <span className="text-muted-foreground">Audited by:</span>
          <span>{auditorName}</span>
        </div>
      )}
      {audit.status !== undefined && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Status:</span>
          <span>{AUDIT_STATUS_LABEL[audit.status] ?? 'Unknown'}</span>
        </div>
      )}
      {duration && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Duration:</span>
          <span>{duration}</span>
        </div>
      )}
      {audit.created_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Created at:</span>
          <FormattedDateTime value={audit.created_at} />
        </div>
      )}
      {audit.finished_at && (
        <div className="flex justify-between gap-8">
          <span className="text-muted-foreground">Finished at:</span>
          <FormattedDateTime value={audit.finished_at} />
        </div>
      )}
    </div>
  );
};

export const humanAuditHandler: TabTypeHandler<Audit> = {
  isDisabled: false,
  getLabel,
  getStatusLabel: () => 'Human Audit',
  PopoverContent: HumanAuditPopoverContent,
};
